import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save, RotateCcw, Users, Search, Key, Trash2, Shield, ShieldOff, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { settingsApi, ratesApi, adminApi } from "@/lib/api";
import { formatCurrency, ITEM_LABELS } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function Settings() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  
  const [settings, setSettings] = useState(null);
  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [editValue, setEditValue] = useState("");
  
  // Admin state
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [tempPassword, setTempPassword] = useState(null);
  const [deleteUserId, setDeleteUserId] = useState(null);

  useEffect(() => { loadData(); }, []);
  
  useEffect(() => {
    if (isAdmin) {
      loadUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, userSearch]);

  const loadData = async () => {
    try {
      const [settingsRes, ratesRes] = await Promise.all([settingsApi.get(), ratesApi.get()]);
      setSettings(settingsRes.data);
      setRates(ratesRes.data);
    } catch (error) { toast.error("Failed to load settings"); }
    finally { setLoading(false); }
  };
  
  const loadUsers = async () => {
    if (!isAdmin) return;
    setLoadingUsers(true);
    try {
      const res = await adminApi.getUsers(userSearch);
      setUsers(res.data);
    } catch (error) { 
      // Silently fail if not admin
    }
    finally { setLoadingUsers(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try { await settingsApi.update(settings); toast.success("Settings saved"); }
    catch (error) { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const handlePriceChange = (type, value) => {
    setSettings({
      ...settings,
      default_paint_prices: { ...settings.default_paint_prices, [type]: parseFloat(value) || 0 },
    });
  };

  const handleRateEdit = async (category, itemKey, currentRate) => {
    if (editingRate === `${category}_${itemKey}`) {
      try {
        await ratesApi.updateRate(category, itemKey, parseFloat(editValue) || currentRate);
        toast.success("Rate updated");
        loadData();
      } catch (error) { toast.error("Failed"); }
      setEditingRate(null);
    } else {
      setEditingRate(`${category}_${itemKey}`);
      setEditValue(String(currentRate));
    }
  };

  const handleRateReset = async (category, itemKey) => {
    try {
      await ratesApi.resetRate(category, itemKey);
      toast.success("Rate reset to default");
      loadData();
    } catch (error) { toast.error("Failed"); }
  };

  // Cabinet sliding-scale tiers aren't a default-vs-override pair like the
  // rates above (no per-item custom_rates entry) - the tiers ARE the
  // setting, so edits live directly on `settings` and save through the
  // existing whole-object "Save Settings" button.
  const handleTierChange = (settingsKey, index, field, value) => {
    setSettings(prev => ({
      ...prev,
      [settingsKey]: prev[settingsKey].map((tier, i) => i === index ? { ...tier, [field]: parseFloat(value) || 0 } : tier),
    }));
  };
  
  const handleResetPassword = async (userId, userEmail) => {
    try {
      const res = await adminApi.resetPassword(userId);
      setTempPassword({ email: userEmail, password: res.data.temporary_password });
      toast.success("Password reset successfully");
    } catch (error) { 
      toast.error("Failed to reset password"); 
    }
  };
  
  const handleToggleRole = async (userId, currentRole) => {
    const newRole = currentRole === "admin" ? "user" : "admin";
    try {
      await adminApi.updateRole(userId, newRole);
      toast.success(`User role changed to ${newRole}`);
      loadUsers();
    } catch (error) { 
      toast.error(error.response?.data?.detail || "Failed to change role"); 
    }
  };
  
  const handleDeleteUser = async () => {
    if (!deleteUserId) return;
    try {
      await adminApi.deleteUser(deleteUserId);
      toast.success("User deleted");
      setDeleteUserId(null);
      loadUsers();
    } catch (error) { 
      toast.error(error.response?.data?.detail || "Failed to delete user"); 
    }
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const renderRatesTable = (ratesObj, category) => {
    if (!ratesObj) return null;
    return (
      <div className="space-y-2">
        {Object.entries(ratesObj).map(([key, info]) => {
          if (info.pricing_model === "flat_plus_unit") {
            return (
              <div key={key} className="flex items-center justify-between p-3 rounded-sm bg-secondary/30">
                <div className="flex-1">
                  <p className="font-medium text-sm">{info.description}</p>
                  <p className="text-xs text-muted-foreground">
                    {key === "cabinetry"
                      ? "Tied to the Cabinet Estimator's Medium-tier rate above - edit it there."
                      : "Edit the setup fee and per-patch rate in the Patch & Texture card below."}
                  </p>
                </div>
                <div className="text-right font-mono text-sm">
                  {info.setup_fee > 0 && <div>{formatCurrency(info.setup_fee)} setup</div>}
                  <div>{formatCurrency(info.per_unit_rate)}/{key === "cabinetry" ? "ln ft" : "patch"}</div>
                </div>
              </div>
            );
          }
          return (
          <div key={key} className={`flex items-center justify-between p-3 rounded-sm ${info.is_custom ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-secondary/30'}`}>
            <div className="flex-1">
              <p className="font-medium text-sm flex items-center gap-2">
                {info.description}
                {info.needs_review && <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-500 border border-amber-500/30">needs review</span>}
                {info.specialty_rate_key && <span className="text-xs px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 border border-blue-500/30">specialty rate</span>}
              </p>
              <p className="text-xs text-muted-foreground">{info.unit === "sq_ft" ? "sq ft/hr" : info.unit === "ln_ft" ? "ln ft/hr" : info.unit === "hours" ? "hours" : "hrs/unit"}</p>
            </div>
            <div className="flex items-center gap-2">
              {editingRate === `${category}_${key}` ? (
                <Input
                  type="number"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="w-20 h-8 font-mono text-sm"
                  autoFocus
                  onKeyDown={(e) => e.key === "Enter" && handleRateEdit(category, key, info.base_rate)}
                />
              ) : (
                <span className="font-mono text-sm w-16 text-right">{info.base_rate}</span>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2"
                onClick={() => handleRateEdit(category, key, info.base_rate)}
              >
                {editingRate === `${category}_${key}` ? "Save" : "Edit"}
              </Button>
              {info.is_custom && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => handleRateReset(category, key)}
                >
                  <RotateCcw className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
          );
        })}
      </div>
    );
  };

  const renderTierTable = (tiers, fields, settingsKey) => (
    <div className="space-y-3">
      {(tiers || []).map((tier, idx) => (
        <div key={idx} className="p-3 rounded-sm bg-secondary/30 space-y-2">
          <div className="flex items-center justify-between gap-4">
            <span className="font-medium text-sm">{tier.label}</span>
            <div className="input-group w-32">
              <Label className="text-xs text-muted-foreground">Max Size</Label>
              {tier.max_size === null ? (
                <Input value="No limit" disabled className="font-mono h-8 text-sm" />
              ) : (
                <Input type="number" value={tier.max_size} onChange={(e) => handleTierChange(settingsKey, idx, 'max_size', e.target.value)} className="font-mono h-8" />
              )}
            </div>
          </div>
          <div className={fields.length === 2 ? "grid grid-cols-2 gap-3" : "grid grid-cols-3 gap-3"}>
            {fields.map(f => (
              <div key={f.key} className="input-group">
                <Label className="text-xs text-muted-foreground">{f.label}</Label>
                <Input type="number" value={tier[f.key]} onChange={(e) => handleTierChange(settingsKey, idx, f.key, e.target.value)} className="font-mono h-8" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  if (loading) return <div className="page-container flex items-center justify-center min-h-screen"><div className="animate-pulse text-muted-foreground">Loading...</div></div>;
  if (!settings) return null;

  return (
    <div className="page-container">
      <header className="app-header">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")}><ArrowLeft className="w-5 h-5" /></Button>
            <div>
              <h1 className="font-heading text-xl font-bold tracking-tight">Settings</h1>
              <p className="text-xs text-muted-foreground">Configure defaults and production rates</p>
            </div>
          </div>
          <Button onClick={handleSave} className="btn-primary" disabled={saving}><Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : "Save Settings"}</Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <Tabs defaultValue="company">
          <TabsList className="mb-6 flex-wrap h-auto">
            <TabsTrigger value="company">Company &amp; Branding</TabsTrigger>
            <TabsTrigger value="rates">Rates &amp; Coverage</TabsTrigger>
            <TabsTrigger value="overhead">Overhead &amp; Profit</TabsTrigger>
            <TabsTrigger value="payment">Payment Terms</TabsTrigger>
            <TabsTrigger value="documents">Document Templates</TabsTrigger>
            {isAdmin && <TabsTrigger value="admin">Admin</TabsTrigger>}
          </TabsList>

          <TabsContent value="company" className="space-y-8">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg">Company Information</CardTitle>
                <CardDescription>Appears on PDF/docx proposals and agreements</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="input-group"><Label className="input-label">Company Name</Label><Input value={settings.company_name} onChange={(e) => setSettings({ ...settings, company_name: e.target.value })} placeholder="Your Company Name" /></div>
                <div className="input-group"><Label className="input-label">Address</Label><Input value={settings.company_address} onChange={(e) => setSettings({ ...settings, company_address: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="input-group"><Label className="input-label">Phone</Label><Input value={settings.company_phone} onChange={(e) => setSettings({ ...settings, company_phone: e.target.value })} /></div>
                  <div className="input-group"><Label className="input-label">Email</Label><Input value={settings.company_email} onChange={(e) => setSettings({ ...settings, company_email: e.target.value })} /></div>
                </div>
                <div className="input-group">
                  <Label className="input-label">State</Label>
                  <Input value={settings.state} placeholder="e.g. TX" onChange={(e) => setSettings({ ...settings, state: e.target.value })} />
                  <p className="text-xs text-muted-foreground mt-1">Used to select mechanic's-lien boilerplate wording in the Agreement document.</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg">Branding</CardTitle>
                <CardDescription>Logo and accent colors for generated documents</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="input-group"><Label className="input-label">Logo URL</Label><Input value={settings.logo_url} onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })} placeholder="https://..." /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="input-group"><Label className="input-label">Primary Color</Label><Input value={settings.brand_primary_color} onChange={(e) => setSettings({ ...settings, brand_primary_color: e.target.value })} placeholder="#F59E0B" /></div>
                  <div className="input-group"><Label className="input-label">Secondary Color</Label><Input value={settings.brand_secondary_color} onChange={(e) => setSettings({ ...settings, brand_secondary_color: e.target.value })} placeholder="#1E293B" /></div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="rates" className="space-y-8">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg">Paint Prices (per gallon)</CardTitle>
                <CardDescription>Used when a project's Material Cost Mode is "By Gallons Used"</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(settings.default_paint_prices || {}).map(([type, price]) => (
                    <div key={type} className="input-group">
                      <Label className="input-label capitalize">{type.replace('_', ' ')}</Label>
                      <Input type="number" value={price} onChange={(e) => handlePriceChange(type, e.target.value)} className="font-mono" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg">Material Cost (% of Labor)</CardTitle>
                <CardDescription>Used when a project's Material Cost Mode is "% of Labor Cost"</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="input-group"><Label className="input-label">Interior (%)</Label><Input type="number" value={settings.material_percent_interior} onChange={(e) => setSettings({ ...settings, material_percent_interior: parseFloat(e.target.value) || 0 })} className="font-mono" /></div>
                  <div className="input-group"><Label className="input-label">Exterior / Deck (%)</Label><Input type="number" value={settings.material_percent_exterior} onChange={(e) => setSettings({ ...settings, material_percent_exterior: parseFloat(e.target.value) || 0 })} className="font-mono" /></div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg">Production Rates</CardTitle>
                <CardDescription>Edit rates to match your crew's productivity. Custom rates are highlighted in amber.</CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="interior">
                  <TabsList className="mb-4">
                    <TabsTrigger value="interior">Interior ({rates?.interior_rates ? Object.keys(rates.interior_rates).length : 0})</TabsTrigger>
                    <TabsTrigger value="exterior">Exterior ({rates?.exterior_rates ? Object.keys(rates.exterior_rates).length : 0})</TabsTrigger>
                    <TabsTrigger value="deck">Deck ({rates?.deck_rates ? Object.keys(rates.deck_rates).length : 0})</TabsTrigger>
                    <TabsTrigger value="cabinets">Cabinets ({(rates?.cabinet_logistics_rates ? Object.keys(rates.cabinet_logistics_rates).length : 0) + (rates?.cabinet_finish_rates ? Object.keys(rates.cabinet_finish_rates).length : 0)})</TabsTrigger>
                  </TabsList>
                  <TabsContent value="interior">{renderRatesTable(rates?.interior_rates, "interior")}</TabsContent>
                  <TabsContent value="exterior">{renderRatesTable(rates?.exterior_rates, "exterior")}</TabsContent>
                  <TabsContent value="deck">{renderRatesTable(rates?.deck_rates, "deck")}</TabsContent>
                  <TabsContent value="cabinets" className="space-y-6">
                    <div>
                      <h4 className="text-xs uppercase tracking-widest text-muted-foreground font-medium border-b border-border pb-2 mb-3">Logistics &amp; Extras (billed hours)</h4>
                      {renderRatesTable(rates?.cabinet_logistics_rates, "cabinet_logistics")}
                    </div>
                    <div>
                      <h4 className="text-xs uppercase tracking-widest text-muted-foreground font-medium border-b border-border pb-2 mb-3">Finish Process (scheduling only, not billed)</h4>
                      {renderRatesTable(rates?.cabinet_finish_rates, "cabinet_finish")}
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg">Cabinet Sliding-Scale Tiers</CardTitle>
                <CardDescription>
                  Small jobs cost more per unit (fixed setup/transport spread over fewer units), large jobs
                  cost less. These are research-based starting numbers - tune them once real jobs run through
                  the app.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h4 className="text-xs uppercase tracking-widest text-muted-foreground font-medium border-b border-border pb-2 mb-3">Linear Foot ($/ln ft)</h4>
                  {renderTierTable(settings.cabinet_linear_foot_tiers, [
                    { key: "lower_rate", label: "Lower Cabinets" },
                    { key: "upper_rate", label: "Upper Cabinets" },
                  ], "cabinet_linear_foot_tiers")}
                </div>
                <div>
                  <h4 className="text-xs uppercase tracking-widest text-muted-foreground font-medium border-b border-border pb-2 mb-3">Per Unit ($/piece)</h4>
                  {renderTierTable(settings.cabinet_per_unit_tiers, [
                    { key: "door_rate", label: "Doors" },
                    { key: "drawer_rate", label: "Drawers" },
                    { key: "box_rate", label: "Boxes" },
                  ], "cabinet_per_unit_tiers")}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg">Patch & Texture Pricing</CardTitle>
                <CardDescription>
                  Flat setup fee plus a per-patch rate, not an hourly formula. $55/patch covers a basic 4"x4"
                  hole with a basic patch only - no sheetrock, no wall framing, no delivery. Different texture
                  styles may cost more; these are starting numbers to tune per job.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="input-group">
                    <Label className="input-label">Setup Fee ($)</Label>
                    <Input type="number" value={settings.patch_texture_setup_fee} onChange={(e) => setSettings({ ...settings, patch_texture_setup_fee: parseFloat(e.target.value) || 0 })} className="font-mono" />
                  </div>
                  <div className="input-group">
                    <Label className="input-label">Per Patch/Crack ($)</Label>
                    <Input type="number" value={settings.patch_texture_per_unit_rate} onChange={(e) => setSettings({ ...settings, patch_texture_per_unit_rate: parseFloat(e.target.value) || 0 })} className="font-mono" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg">Coat Scaling</CardTitle>
                <CardDescription>
                  Each item's per-coat production rate (shown in Production Rates above) already accounts for
                  diminishing-returns coat scaling and your selected prep level - there's no single global
                  percentage anymore, since coverage items (walls, siding) and count items (doors, windows)
                  scale in opposite directions per coat.
                </CardDescription>
              </CardHeader>
            </Card>
          </TabsContent>

          <TabsContent value="overhead" className="space-y-8">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg">Labor Rates</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="input-group"><Label className="input-label">Default Labor Rate ($/hr)</Label><Input type="number" value={settings.default_labor_rate} onChange={(e) => setSettings({ ...settings, default_labor_rate: parseFloat(e.target.value) || 0 })} className="font-mono" /></div>
                <div className="input-group">
                  <Label className="input-label">
                    Faux/Mural Labor Rate ($/hr)
                    {!settings.faux_mural_labor_rate && <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-500 border border-amber-500/30 normal-case tracking-normal">needs review</span>}
                  </Label>
                  <Input type="number" value={settings.faux_mural_labor_rate ?? ""} placeholder="Not set - falls back to standard labor rate" onChange={(e) => setSettings({ ...settings, faux_mural_labor_rate: e.target.value === "" ? null : parseFloat(e.target.value) || 0 })} className="font-mono" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg">Overhead &amp; Profit</CardTitle>
                <CardDescription>Applied to every project's total, on top of labor and materials</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="input-group"><Label className="input-label">Overhead Factor</Label><Input type="number" step="0.1" value={settings.overhead_factor} onChange={(e) => setSettings({ ...settings, overhead_factor: parseFloat(e.target.value) || 0 })} className="font-mono" /></div>
                  <div className="input-group"><Label className="input-label">Profit (%)</Label><Input type="number" value={settings.profit_percent} onChange={(e) => setSettings({ ...settings, profit_percent: parseFloat(e.target.value) || 0 })} className="font-mono" /></div>
                  <div className="input-group"><Label className="input-label">Incentive (%)</Label><Input type="number" value={settings.incentive_percent} onChange={(e) => setSettings({ ...settings, incentive_percent: parseFloat(e.target.value) || 0 })} className="font-mono" /></div>
                </div>
                <div className="input-group">
                  <Label className="input-label">Non-Productive Time (% of labor hours each)</Label>
                  <div className="grid grid-cols-5 gap-2 mt-1">
                    <div><Label className="text-xs text-muted-foreground">Breaks</Label><Input type="number" value={settings.nonproductive_breaks_percent} onChange={(e) => setSettings({ ...settings, nonproductive_breaks_percent: parseFloat(e.target.value) || 0 })} className="font-mono h-9" /></div>
                    <div><Label className="text-xs text-muted-foreground">Cleanup</Label><Input type="number" value={settings.nonproductive_cleanup_percent} onChange={(e) => setSettings({ ...settings, nonproductive_cleanup_percent: parseFloat(e.target.value) || 0 })} className="font-mono h-9" /></div>
                    <div><Label className="text-xs text-muted-foreground">PM</Label><Input type="number" value={settings.nonproductive_pm_percent} onChange={(e) => setSettings({ ...settings, nonproductive_pm_percent: parseFloat(e.target.value) || 0 })} className="font-mono h-9" /></div>
                    <div><Label className="text-xs text-muted-foreground">Closeout</Label><Input type="number" value={settings.nonproductive_closeout_percent} onChange={(e) => setSettings({ ...settings, nonproductive_closeout_percent: parseFloat(e.target.value) || 0 })} className="font-mono h-9" /></div>
                    <div><Label className="text-xs text-muted-foreground">Travel</Label><Input type="number" value={settings.nonproductive_travel_percent} onChange={(e) => setSettings({ ...settings, nonproductive_travel_percent: parseFloat(e.target.value) || 0 })} className="font-mono h-9" /></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="payment" className="space-y-8">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg">Payment Schedule</CardTitle>
                <CardDescription>Must add up to 100%. Applied to every project's total.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div className="input-group"><Label className="input-label">Deposit (%)</Label><Input type="number" value={settings.deposit_percent} onChange={(e) => setSettings({ ...settings, deposit_percent: parseFloat(e.target.value) || 0 })} className="font-mono" /></div>
                  <div className="input-group"><Label className="input-label">Midway Draw (%)</Label><Input type="number" value={settings.midway_draw_percent} onChange={(e) => setSettings({ ...settings, midway_draw_percent: parseFloat(e.target.value) || 0 })} className="font-mono" /></div>
                  <div className="input-group"><Label className="input-label">Completion (%)</Label><Input type="number" value={settings.completion_percent} onChange={(e) => setSettings({ ...settings, completion_percent: parseFloat(e.target.value) || 0 })} className="font-mono" /></div>
                </div>
                {(settings.deposit_percent + settings.midway_draw_percent + settings.completion_percent) !== 100 && (
                  <p className="text-xs text-amber-500 mt-2">These currently add up to {settings.deposit_percent + settings.midway_draw_percent + settings.completion_percent}%, not 100%.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="documents" className="space-y-8">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg">Proposal &amp; Agreement Templates</CardTitle>
                <CardDescription>
                  Editable text used to generate client documents. Supports {"{{"}"token"{"}}"} placeholders
                  (client_name, total_price, state, etc). These are starting points, not legal advice -
                  review with your own counsel before sending real documents, especially the mechanic's-lien
                  disclosure below, which is state-specific.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  ["work_standard", "Work Standard"],
                  ["customer_responsibility", "Customer Responsibility"],
                  ["warranty", "Warranty"],
                  ["surface_prep_description", "Surface Prep Description"],
                  ["included_items", "Included Items"],
                  ["excluded_items", "Excluded Items"],
                  ["subcontracting_clause", "Subcontracting Clause"],
                  ["change_orders_clause", "Change Orders Clause"],
                  ["insurance_clause", "Insurance Clause"],
                  ["delay_clause", "Delay Clause"],
                  ["lien_release_clause", "Lien Release Clause"],
                  ["collection_interest_clause", "Collection & Interest Clause"],
                  ["severability_clause", "Severability Clause"],
                  ["mechanics_lien_disclosure", "Mechanic's Lien Disclosure"],
                  ["cancellation_rights_notice", "Cancellation Rights Notice"],
                ].map(([key, label]) => (
                  <div key={key} className="input-group">
                    <Label className="input-label">{label}</Label>
                    <textarea
                      className="w-full min-h-20 p-3 text-sm rounded-sm bg-input border border-border font-mono"
                      value={settings.document_templates?.[key] || ""}
                      onChange={(e) => setSettings({
                        ...settings,
                        document_templates: { ...settings.document_templates, [key]: e.target.value },
                      })}
                    />
                  </div>
                ))}
                <div className="input-group">
                  <Label className="input-label">Proposal Expiration (days)</Label>
                  <Input
                    type="number"
                    className="font-mono w-32"
                    value={settings.document_templates?.proposal_expiration_days ?? 30}
                    onChange={(e) => setSettings({
                      ...settings,
                      document_templates: { ...settings.document_templates, proposal_expiration_days: parseInt(e.target.value) || 0 },
                    })}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {isAdmin && (
            <TabsContent value="admin">
              <Card className="border-amber-500/50">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-amber-500" />
                    <CardTitle className="font-heading text-lg">Admin Panel</CardTitle>
                  </div>
                  <CardDescription>Manage users, reset passwords, and more</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Search */}
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Search by email or name..."
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                  </div>

                  {/* Temporary Password Display */}
                  {tempPassword && (
                    <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-sm space-y-2">
                      <p className="text-sm font-medium text-green-400">Password Reset Successful</p>
                      <p className="text-xs text-muted-foreground">Give this temporary password to: <strong>{tempPassword.email}</strong></p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 p-2 bg-black/50 rounded font-mono text-sm">{tempPassword.password}</code>
                        <Button variant="outline" size="sm" onClick={() => copyToClipboard(tempPassword.password)}>
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setTempPassword(null)} className="text-xs">Dismiss</Button>
                    </div>
                  )}

                  {/* Users List */}
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">{users.length} user{users.length !== 1 ? 's' : ''} found</p>
                    {loadingUsers ? (
                      <div className="text-center py-4 text-muted-foreground">Loading...</div>
                    ) : (
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {users.map((u) => (
                          <div key={u.id} className={`flex items-center justify-between p-3 rounded-sm ${u.role === 'admin' ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-secondary/30'}`}>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-sm truncate">{u.name || 'No name'}</p>
                                {u.role === 'admin' && <span className="text-xs bg-amber-500 text-black px-1.5 py-0.5 rounded">ADMIN</span>}
                              </div>
                              <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                              <p className="text-xs text-muted-foreground">{u.project_count} project{u.project_count !== 1 ? 's' : ''} • Joined {new Date(u.created_at).toLocaleDateString()}</p>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleResetPassword(u.id, u.email)}
                                title="Reset Password"
                              >
                                <Key className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleToggleRole(u.id, u.role)}
                                title={u.role === 'admin' ? 'Remove Admin' : 'Make Admin'}
                              >
                                {u.role === 'admin' ? <ShieldOff className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive"
                                onClick={() => setDeleteUserId(u.id)}
                                title="Delete User"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </main>
      
      {/* Delete User Confirmation */}
      <AlertDialog open={!!deleteUserId} onOpenChange={() => setDeleteUserId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the user and ALL their projects. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteUser} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
